import React, { FunctionComponent, useMemo, useState, useEffect, useCallback } from "react";
import { useIntl, IntlShape } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  IconButton,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  SettingsIcon,
} from "@chakra-ui/icons";

import { groupBy, sortBy } from "lodash";

import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { SITE_ALARM_TYPES_QUERY } from "graphql/queries";
import { ACK_CURRENT_ALARM_TYPES_MUTATION } from "graphql/mutations";
import {
  AckAlarmTypesNow,
  AckAlarmTypesNowVariables,
  SiteConfig_siteConfig_alarmsConf,
  SiteAlarmTypes,
  SiteAlarmTypesVariables,
  SiteAlarmTypes_siteAlarmTypes,
} from "graphql/generated";

import { useLiveMeasurements } from "scenes/site/hooks";

import AlarmTypeGroup from "./AlarmTypeGroup";
import AlarmTypeDetail from "./AlarmTypeDetail";
import AlarmGroupDetail from "./AlarmGroupDetail";


interface IProps {
  alarmsConf: SiteConfig_siteConfig_alarmsConf;
  timezone: string;
  siteId: string;
  basePath: string;
}


type TAlarmTypesGroup = [string, SiteAlarmTypes_siteAlarmTypes[]][];


export const groupName = (intl: IntlShape, name: string): string => {
  switch(name) {
    case "battery":
    case "bess": return intl.formatMessage({
      id: "alarms__group__bess",
      defaultMessage: "Battery storage",
    });
    case "breaker": return intl.formatMessage({
      id: "alarms__group__breaker",
      defaultMessage: "Circuit breakers",
    });
    case "chp": return intl.formatMessage({
      id: "alarms__group__chp",
      defaultMessage: "Cogeneration",
    });
    case "converter": return intl.formatMessage({
      id: "alarms__group__converter",
      defaultMessage: "Converters",
    });
    case "misc": return intl.formatMessage({
      id: "alarms__group__misc",
      defaultMessage: "Miscellaneous",
    });
    case "modes": return intl.formatMessage({
      id: "alarms__group__modes",
      defaultMessage: "Modes",
    });
    case "system": return intl.formatMessage({
      id: "alarms__group__system",
      defaultMessage: "System",
    });
    case "ups": return intl.formatMessage({
      id: "alarms__group__ups",
      defaultMessage: "UPS",
    });
    case "hvac": return intl.formatMessage({
      id: "alarms__group__hvac",
      defaultMessage: "HVAC",
    });
    case "test": return intl.formatMessage({
      id: "alarms__group__test",
      defaultMessage: "Test",
    });
    default: return name;
  }
}


const AlarmTypes: FunctionComponent<IProps> = ({
  alarmsConf,
  timezone,
  siteId,
  basePath,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useLiveMeasurements(siteId, "alarm_types", { clearOnReconnect: true });
  const { data: seedData, loading } = useQuery<
    SiteAlarmTypes,
    SiteAlarmTypesVariables
  >(SITE_ALARM_TYPES_QUERY, { variables: { siteId } });
  const [ ackAlarmTypesNowMutation ] = useMutation<AckAlarmTypesNow, AckAlarmTypesNowVariables>(ACK_CURRENT_ALARM_TYPES_MUTATION);
  const alarmTypes = useMemo<SiteAlarmTypes_siteAlarmTypes[]>(() =>
    Object.values(data).length > 0
    ? Object.values(data)
    : seedData?.siteAlarmTypes ?? [],
    [data, seedData?.siteAlarmTypes]
  );
  const alarmTypesByNs = useMemo<TAlarmTypesGroup>(() => sortBy(Object.entries(groupBy(alarmTypes, "ns")), _ => groupName(intl, _[0])), [alarmTypes, intl]);
  const [detailedType, setDetailedType] = useState<SiteAlarmTypes_siteAlarmTypes | undefined>(undefined);
  const [detailedNs, setDetailedNs] = useState<string | undefined>(undefined);
  const settingsButtonBg = useColorModeValue("white", "gray.800");
  const alarmTypeId = useMemo<string | null>(() => {
    const slug = location.pathname.substring(basePath.length);
    const m = slug.match(/alarms(\/([0-9a-z-]+))?/);

    return m && m[2];
  }, [location.pathname, basePath]);

  const acknowledgeAll = useCallback(() => {
    ackAlarmTypesNowMutation({
      variables: {
        siteId,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alarmTypeDetail = useMemo(() => <AlarmTypeDetail
    siteId={siteId}
    timezone={timezone}
    item={detailedType}
    onClosed={() => setDetailedType(undefined)}
  />, [siteId, timezone, detailedType]);

  const alarmGroupDetail = useMemo(() => <AlarmGroupDetail
    siteId={siteId}
    ns={detailedNs}
    name={detailedNs && groupName(intl, detailedNs)}
    onClosed={() => setDetailedNs(undefined)}
  />, [siteId, detailedNs, intl]);

  useEffect(() => {
    const alarmType = alarmTypes.find(_ => _.condition === alarmTypeId);

    alarmType && setDetailedType(alarmType);
  }, [alarmTypes, alarmTypeId]);

  useEffect(() => {
    if (detailedType) {
      navigate(`${basePath}/alarms/${detailedType.condition}`)
    } else {
      navigate(`${basePath}/alarms`)
    }
  }, [basePath, navigate, detailedType]);

  return (
    <>

      <Flex direction="row" flexWrap="wrap" justifyContent="stretch" alignItems="flex-start">
        {
          loading
          ? <Spinner size="lg" m="4" />
          : alarmTypesByNs.map(([ns, alarm_types]) => (
              <AlarmTypeGroup
                key={ns}
                ns={ns}
                name={groupName(intl, ns)}
                timezone={timezone}
                items={alarm_types}
                setDetailedType={setDetailedType}
                setDetailedNs={setDetailedNs}
              />
            ))
        }
      </Flex>
      <Flex direction="row" flexWrap="wrap" mt={3}>
        <IconButton
          aria-label="Alerts"
          bg={settingsButtonBg}
          m={1}
          onClick={() => setDetailedNs("")}
          icon={<SettingsIcon />}
          title={intl.formatMessage({
            id: "alarms__alarm_ns__settings",
            defaultMessage: "Settings",
          })}
        />
        <Button
          colorScheme={"enposol"}
          m={1}
          onClick={() => acknowledgeAll()}
        >
          {intl.formatMessage({
            id: "alarms__acknowledge_all",
            defaultMessage: "Acknowledge all alarms"
          })}
        </Button>
      </Flex>

      {alarmGroupDetail}
      {alarmTypeDetail}

    </>
  );
};

export default AlarmTypes;
